// Pattern to match [SOURCE-<id>] where <id> is any non-bracket characters
// Also matches an optional preceding backslash (LLMs sometimes escape the bracket as \[SOURCE-x])
const CITATION_PATTERN = /\\?\[SOURCE-([^\]]+)\]/g;

// Pattern to match reference definitions: [SOURCE-x]: url
const REFERENCE_DEFINITION_PATTERN = /^\[SOURCE-([^\]]+)\]:\s*(.+)$/gm;

// Pattern to match reference-style links: [text][SOURCE-x]
const REFERENCE_LINK_PATTERN = /\[([^\]]+)\]\[SOURCE-([^\]]+)\]/g;

// Pattern to check if text is a SOURCE reference itself
const SOURCE_TEXT_PATTERN = /^SOURCE-([^\]]+)$/;

/**
 * Pre-processes markdown to:
 * 1. Convert reference-style citation links to inline format
 * 2. Convert [SOURCE-x] markers to <citation> HTML tags
 *
 * The <citation> tags are then rendered by Streamdown's component system
 * using the allowedTags and components props.
 */
export function preprocessMarkdownCitations(markdown: string): string {
  let processed = markdown;

  // Step 1: Handle reference-style citation links
  // Extract reference definitions and build a map
  const definitions = new Map<string, string>();

  Array.from(processed.matchAll(REFERENCE_DEFINITION_PATTERN)).forEach((match) => {
    const sourceId = match[1];
    const url = match[2].trim();
    definitions.set(sourceId, url);
  });

  if (definitions.size > 0) {
    // Replace reference-style links with inline links + citation marker
    processed = processed.replace(REFERENCE_LINK_PATTERN, (_fullMatch, linkText, sourceId) => {
      // Check if the link text is itself a SOURCE reference
      const linkTextSourceMatch = SOURCE_TEXT_PATTERN.exec(linkText);
      if (linkTextSourceMatch) {
        // Both are citations - output them as separate citation markers with space between
        return `[SOURCE-${linkTextSourceMatch[1]}] [SOURCE-${sourceId}]`;
      }

      const url = definitions.get(sourceId);
      if (url) {
        // Convert to inline link followed by citation marker
        return `[${linkText}](${url}) [SOURCE-${sourceId}]`;
      }
      // If no definition found, keep original
      return _fullMatch;
    });

    // Remove the reference definitions from the end of the markdown
    processed = processed.replace(REFERENCE_DEFINITION_PATTERN, '');

    // Clean up any trailing whitespace from removed definitions
    processed = processed.trimEnd();
  }

  // Step 2: Convert [SOURCE-x] markers to <citation> HTML tags
  // Note: HTML parsers lowercase attributes, so we use lowercase 'sourceid'
  processed = processed.replace(CITATION_PATTERN, (_match, sourceId) => {
    return `<citation sourceid="${sourceId}"></citation>`;
  });

  return processed;
}
